import { z } from 'zod';
import { moneyAmountSchema, moneySchema } from './catalog.js';
import { itemNames, pageFields, pageQuery } from './common.js';
import { quantitySchema } from './inventory.js';
import { optionalId } from './optional-id.js';
import { paymentInputSchema } from './sales.js';

/** Online store contracts (Prompt 16). See BUSINESS_RULES.md BR-140…BR-149. */
const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v == null || v === '' ? null : v));
const named = { nameEn: z.string().nullable(), nameAr: z.string().nullable() };

/**
 * What Google shows for a product, category or collection page (owner decision 2026-11-03):
 * the result's title and description, and a product's pictures' alternative text. Optional:
 * empty = filled automatically from the name and description.
 */
export const SEO_TITLE_MAX = 70;
export const SEO_DESCRIPTION_MAX = 320;
export const IMAGE_ALT_MAX = 125;
/** A text that may be left out on save: absent = unchanged, '' = cleared (null), trimmed. */
const keptText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((v) => v || null)
    .optional();
const seoFields = {
  seoTitleAr: z.string().nullable().default(null),
  seoTitleEn: z.string().nullable().default(null),
  seoDescriptionAr: z.string().nullable().default(null),
  seoDescriptionEn: z.string().nullable().default(null),
};
const saveSeoFields = {
  seoTitleAr: keptText(SEO_TITLE_MAX),
  seoTitleEn: keptText(SEO_TITLE_MAX),
  seoDescriptionAr: keptText(SEO_DESCRIPTION_MAX),
  seoDescriptionEn: keptText(SEO_DESCRIPTION_MAX),
};
const imageAltFields = {
  imageAltAr: z.string().nullable().default(null),
  imageAltEn: z.string().nullable().default(null),
};

/**
 * Readable store addresses (owner decision 2026-11-03, migration 0107): a product, category,
 * collection or brand page is `/products/{handle}`, `/categories/{handle}`,
 * `/collections/{handle}`, `/brands/{handle}`. A handle is lower case, letters of any language
 * (Arabic included: Google reads percent-encoded addresses), digits and single `-` between
 * them; at most 100 characters; never shaped like a record id.
 */
export const STORE_HANDLE_MAX = 100;
const HANDLE_PATTERN = /^[\p{L}\p{N}]+(?:-[\p{L}\p{N}]+)*$/u;
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Kinds of store records with a handle (`online_handle_history.kind`). */
export const STORE_HANDLE_KINDS = ['product', 'category', 'collection', 'brand'] as const;
export type StoreHandleKind = (typeof STORE_HANDLE_KINDS)[number];

/**
 * What a merchant typed as a handle, tidied: trimmed, lower case, Arabic-Indic digits as 0-9,
 * Arabic marks and the tatweel dropped, spaces and `_` as `-`, repeated `-` as one, none at
 * either end. Whatever else is left must pass `storeHandleSchema`.
 */
export function normalizeStoreHandle(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[ً-ٰٟـ]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** A handle the merchant gives (console); invalid → 400 `VALIDATION_FAILED`. */
export const storeHandleSchema = z
  .string()
  .max(STORE_HANDLE_MAX * 2)
  .transform(normalizeStoreHandle)
  .pipe(
    z
      .string()
      .min(1, { error: 'A page address is required.' })
      .max(STORE_HANDLE_MAX)
      .regex(HANDLE_PATTERN, { error: 'Use letters, digits and dashes only.' })
      .refine((v) => !UUID_SHAPE.test(v), { error: 'Use a readable address.' }),
  );

/** Whether a path part could be a handle or a record id (cheap guard before a lookup). */
export const isStoreHandleKey = (v: string) =>
  v.length > 0 &&
  v.length <= STORE_HANDLE_MAX &&
  (UUID_SHAPE.test(v) || (HANDLE_PATTERN.test(v) && v === v.toLowerCase()));

/** Kinds of store page that list products of one group, and where their pages are. */
export const STORE_GROUP_PATHS = {
  category: 'categories',
  collection: 'collections',
  brand: 'brands',
} as const;
export type StoreGroupKind = keyof typeof STORE_GROUP_PATHS;
/** A product's page after the store's address: `/products/{handle}` (the id while it has none). */
export const storeProductPath = (p: { productId: string; handle?: string | null }) =>
  `/products/${encodeURIComponent(p.handle || p.productId)}`;
/** A category, collection or brand page after the store's address, e.g. `/categories/coffee`. */
export const storeGroupPath = (kind: StoreGroupKind, g: { id: string; handle?: string | null }) =>
  `/${STORE_GROUP_PATHS[kind]}/${encodeURIComponent(g.handle || g.id)}`;

const STORE_STATUSES = ['draft', 'published', 'paused'] as const;
export const ONLINE_ORDER_STATUSES = [
  'pending_payment',
  'placed',
  'confirmed',
  'paid',
  'preparing',
  'ready',
  'out_for_delivery',
  'completed',
  'expired',
  'payment_failed',
  'cancelled',
  'needs_attention',
] as const;
export type OnlineOrderStatus = (typeof ONLINE_ORDER_STATUSES)[number];
const FULFILLMENTS = ['pickup', 'delivery'] as const;
/** When the shopper picks the delivery area (ADR-0141): on the checkout page or on entering. */
const AREA_PROMPTS = ['checkout', 'entry'] as const;
/** gateway: paid online before fulfilment · on_receipt: collected on hand-over (ADR-0118). */
const PAYMENT_MODES = ['gateway', 'on_receipt'] as const;
const GATEWAY_STATUSES = ['initiated', 'succeeded', 'failed', 'cancelled', 'expired'] as const;

// ---------------------------------------------------------------- manager: store settings
/**
 * Store addresses that cannot be chosen: paths of the storefront app (`/store/pay/...`,
 * `/store/links/...`), the platform's own apps and words kept for later use.
 */
export const RESERVED_STORE_SLUGS: readonly string[] = [
  'pay',
  'links',
  'assets',
  'api',
  'admin',
  'store',
  'manager',
  'pos',
  'platform',
  'device',
  'online',
  'start',
  'pricing',
  'www',
  'mail',
  'app',
  'apps',
  'help',
  'support',
  'arabat',
  // Host names of the platform's own DNS zone (stores answer at `{slug}.arabatapp.com`, ADR-0136).
  'stores',
  'send',
  'rsend',
  'smtp',
  'ftp',
  'cdn',
  'static',
  'status',
  'docs',
  'blog',
  'dev',
  'staging',
  'ns1',
  'ns2',
];
export const STORE_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,40}$/;
/**
 * Whether a store address can be its own sub-domain (ADR-0136): a valid DNS label (no dash at
 * the end, no `xx--` prefix kept for encoded names) that is not reserved. Others keep the
 * `/store/{slug}` address.
 */
/** A store's permanent code (ADR-0137), e.g. `st-4k9m2p`; never a store address. */
export const STORE_CODE_PATTERN = /^st-[a-z0-9]{6}$/;
export const isStoreSubdomain = (slug: string): boolean =>
  /^[a-z0-9](?:[a-z0-9-]{0,40}[a-z0-9])?$/.test(slug) &&
  slug.slice(2, 4) !== '--' &&
  !RESERVED_STORE_SLUGS.includes(slug);
// ---------------------------------------------------------------- hours and slots (ADR-0123)
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = (typeof WEEKDAYS)[number];
/** HH:MM; 24:00 is allowed as a closing time (open until midnight). */
const clock = z.string().regex(/^(([01]\d|2[0-3]):[0-5]\d|24:00)$/, { error: 'Use HH:MM.' });
const hoursRangeSchema = z
  .object({ open: clock, close: clock })
  .refine((r) => r.close > r.open, { error: 'Closing time must be after opening time.' });
/** Opening hours per weekday; an empty list = closed that day. Null = always open. */
const dayRanges = z.array(hoursRangeSchema).max(4);
const hoursSchema = z
  .object({
    mon: dayRanges,
    tue: dayRanges,
    wed: dayRanges,
    thu: dayRanges,
    fri: dayRanges,
    sat: dayRanges,
    sun: dayRanges,
  })
  .nullable();
export type StoreHours = z.infer<typeof hoursSchema>;

export const onlineStoreSchema = z.object({
  slug: z.string(),
  status: z.enum(STORE_STATUSES),
  nameEn: z.string().nullable(),
  nameAr: z.string().nullable(),
  taglineEn: z.string().nullable(),
  taglineAr: z.string().nullable(),
  announcementEn: z.string().nullable(),
  announcementAr: z.string().nullable(),
  primaryColor: z.string(),
  contactPhone: z.string().nullable(),
  contactEmail: z.string().nullable(),
  fulfillmentBranchId: z.string().nullable(),
  pickupEnabled: z.boolean(),
  deliveryEnabled: z.boolean(),
  deliveryFee: moneySchema,
  minimumOrder: moneySchema,
  reservationMinutes: z.number().int(),
  /** Shoppers may order without paying online and pay on hand-over (ADR-0118). */
  payOnReceiptEnabled: z.boolean(),
  /** Store logo and banner (ADR-0121), served by the API; null when not uploaded. */
  logoUrl: z.string().nullable(),
  bannerUrl: z.string().nullable(),
  /** Leave products with nothing available out of the storefront. */
  hideOutOfStock: z.boolean(),
  /**
   * Legacy (ADR-0141): hours, scheduling, the pickup / delivery switches and the fulfilment
   * branch now belong to each serving branch; these store fields are kept but no longer used.
   */
  hours: hoursSchema,
  schedulingEnabled: z.boolean(),
  slotMinutes: z.number().int(),
  /** Days shoppers may schedule in, today included (1 = today only, 2 = today and tomorrow). */
  scheduleDays: z.number().int(),
  slotCapacity: z.number().int().nullable(),
  /** When the shopper picks the delivery area (ADR-0141). */
  areaPrompt: z.enum(AREA_PROMPTS).default('checkout'),
  notifyEmail: z.boolean(),
  /** WhatsApp the store's number about new orders (ADR-0138). */
  notifyWhatsapp: z.boolean(),
  notifyWhatsappPhone: z.string().nullable(),
  /** WhatsApp messages can be sent from this server (a provider is connected). */
  whatsappAvailable: z.boolean(),
  /** Shoppers may create an account and sign in (ADR-0124); guests still order. */
  accountsEnabled: z.boolean(),
  /** Google Search Console site verification token; null = none. */
  googleSiteVerification: z.string().nullable().default(null),
  /** «Connect with Google» is configured on this server (GOOGLE_OAUTH_CLIENT_ID / _SECRET). */
  googleConnectAvailable: z.boolean().default(false),
  /** Payment methods available online (`availableOnline` and a gateway provider). */
  paymentMethods: z.array(
    z.object({
      id: z.string(),
      ...named,
      provider: z.string(),
      /** Only the mock gateway processes payments today; others need credentials and approval. */
      ready: z.boolean(),
    }),
  ),
  url: z.string(),
  version: z.number().int(),
});
export type OnlineStore = z.infer<typeof onlineStoreSchema>;

/**
 * Google Search Console's site verification code: the merchant pastes the bare token or the
 * whole `<meta name="google-site-verification" content="…" />` tag; only the token is kept.
 */
export const GOOGLE_SITE_VERIFICATION_PATTERN = /^[A-Za-z0-9_-]{10,100}$/;
/** The token inside a pasted value (bare token or the meta tag); null when nothing usable. */
export function extractGoogleSiteVerification(raw: string): string | null {
  const value = raw.trim();
  if (value === '') return null;
  if (value.startsWith('<')) {
    if (!/^<meta\b[^<>]*>$/i.test(value)) return null;
    if (!/\bname\s*=\s*["']google-site-verification["']/i.test(value)) return null;
    const content = /\bcontent\s*=\s*["']([^"']*)["']/i.exec(value)?.[1]?.trim() ?? '';
    return GOOGLE_SITE_VERIFICATION_PATTERN.test(content) ? content : null;
  }
  return GOOGLE_SITE_VERIFICATION_PATTERN.test(value) ? value : null;
}
/** Absent = unchanged; null or '' = remove; otherwise a valid token or the meta tag holding one. */
const googleSiteVerificationField = z
  .string()
  .max(500)
  .nullish()
  .transform((v, ctx) => {
    if (v === undefined) return undefined;
    if (v === null || v.trim() === '') return null;
    const token = extractGoogleSiteVerification(v);
    if (!token) {
      ctx.addIssue({
        code: 'custom',
        message: 'Paste the code from Google Search Console (the HTML tag or its content value).',
      });
      return z.NEVER;
    }
    return token;
  });
export const saveOnlineStoreSchema = z
  .object({
    slug: z
      .string()
      .trim()
      .toLowerCase()
      .regex(STORE_SLUG_PATTERN, {
        error: 'Letters, digits and dashes (2–41 characters).',
      })
      .refine((slug) => !RESERVED_STORE_SLUGS.includes(slug) && !STORE_CODE_PATTERN.test(slug), {
        error: 'This address is reserved.',
      }),
    status: z.enum(STORE_STATUSES),
    nameEn: text(80),
    nameAr: text(80),
    taglineEn: text(160),
    taglineAr: text(160),
    announcementEn: text(300),
    announcementAr: text(300),
    primaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    contactPhone: text(40),
    contactEmail: z
      .email()
      .nullish()
      .or(z.literal(''))
      .transform((v) => v || null),
    fulfillmentBranchId: optionalId(),
    /** Legacy (ADR-0141): seeds the main branch row of a new store only. */
    pickupEnabled: z.boolean().default(true),
    deliveryEnabled: z.boolean().default(false),
    deliveryFee: moneyAmountSchema,
    minimumOrder: moneyAmountSchema,
    reservationMinutes: z.number().int().min(5).max(1440),
    payOnReceiptEnabled: z.boolean().default(true),
    hideOutOfStock: z.boolean().default(false),
    hours: hoursSchema.default(null),
    schedulingEnabled: z.boolean().default(false),
    slotMinutes: z.number().int().min(15).max(240).default(30),
    scheduleDays: z.number().int().min(1).max(14).default(7),
    slotCapacity: z
      .number()
      .int()
      .min(1)
      .max(1000)
      .nullish()
      .transform((v) => v ?? null),
    areaPrompt: z.enum(AREA_PROMPTS).default('checkout'),
    notifyEmail: z.boolean().default(true),
    notifyWhatsapp: z.boolean().default(false),
    accountsEnabled: z.boolean().default(true),
    googleSiteVerification: googleSiteVerificationField,
    version: z.number().int().nonnegative(),
  })
  .refine((s) => Boolean(s.nameAr || s.nameEn), {
    error: 'A store name is required.',
    path: ['nameAr'],
  });
export type SaveOnlineStore = z.input<typeof saveOnlineStoreSchema>;

/**
 * The store's WhatsApp number changes only with a code sent to it on WhatsApp (ADR-0138
 * amendment 9): ask for the code, then type it back within the time allowed.
 */
export const whatsappCodeRequestSchema = z.object({ phone: z.string().trim().min(6).max(30) });
export const whatsappCodeSentSchema = z.object({
  phone: z.string(),
  expiresAt: z.string(),
  resendAfterSeconds: z.number().int(),
});
export type WhatsappCodeSent = z.infer<typeof whatsappCodeSentSchema>;
export const whatsappCodeConfirmSchema = z.object({
  phone: z.string().trim().min(6).max(30),
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, { error: 'The 6-digit code.' }),
});

// ---------------------------------------------------------------- own domains (ADR-0137)
const STORE_DOMAIN_STATUSES = ['pending', 'verifying', 'active', 'failed', 'removed'] as const;
export type StoreDomainStatus = (typeof STORE_DOMAIN_STATUSES)[number];
/** A domain as typed (`https://www.Shop.com/x` → `shop.com`), without `www.`. */
function normalizeDomain(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .replace(/[/?#].*$/, '')
    .replace(/:\d+$/, '')
    .replace(/\.$/, '')
    .replace(/^www\./, '');
}
const DOMAIN_PATTERN = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;
/** `POST /manager/online/domain`: the domain the merchant bought. */
export const connectStoreDomainSchema = z.object({
  domain: z
    .string()
    .max(253)
    .transform(normalizeDomain)
    .pipe(z.string().regex(DOMAIN_PATTERN, { error: 'Type a domain such as your-store.com.' })),
});
/** `PUT /manager/online/domain/primary`: shoppers' main address is the domain, or not. */
export const storeDomainPrimarySchema = z.object({ primary: z.boolean() });
/** One host name of a connected domain as Cloudflare sees it. */
const storeDomainHostSchema = z.object({
  hostname: z.string(),
  /** pending | active | failed (the host name is reached through Arabat). */
  status: z.enum(['pending', 'active', 'failed']),
  /** pending | active | failed (the certificate). */
  certificate: z.enum(['pending', 'active', 'failed']),
  errors: z.array(z.string()),
});
/** A DNS record the merchant adds at their domain provider. */
const dnsRecordSchema = z.object({
  type: z.string(),
  name: z.string(),
  value: z.string(),
  /**
   * required: the store answers through it; root: the bare domain (optional); verify: speeds up
   * checks; ownership: proves the merchant holds the domain (RS-29, shown only while needed).
   */
  purpose: z.enum(['required', 'root', 'verify', 'ownership']),
});
const storeDomainSchema = z.object({
  domain: z.string(),
  status: z.enum(STORE_DOMAIN_STATUSES),
  isPrimary: z.boolean(),
  www: storeDomainHostSchema,
  root: storeDomainHostSchema,
  /**
   * The TXT ownership proof (RS-29), null when the CNAME is enough: needed when the domain was
   * connected to another merchant before, or Cloudflare already had a host name for it. The
   * domain cannot work or become the main address until `verified`.
   */
  ownership: z.object({ name: z.string(), value: z.string(), verified: z.boolean() }).nullable(),
  /**
   * The bare domain (`{domain}`, record `@`) as the DNS answers it now, read by the server at each
   * request: `resolves` = it has an address at all, `pointsToUs` = its CNAME is the target or its
   * addresses are the target's. Not needed for the domain to work (`www` is the main address),
   * but shoppers typing the bare name reach nothing without it. Null when it cannot be checked
   * (no target on this server).
   */
  apex: z.object({ resolves: z.boolean(), pointsToUs: z.boolean() }).nullable().default(null),
  records: z.array(dnsRecordSchema),
  lastCheckedAt: z.string().nullable(),
  activatedAt: z.string().nullable(),
});
export type StoreDomain = z.infer<typeof storeDomainSchema>;
/** `GET /manager/online/domain`: every address of the store. */
export const storeAddressesSchema = z.object({
  /** Connecting a domain works on this server (Cloudflare for SaaS configured). */
  available: z.boolean(),
  code: z.string(),
  /** Never changes: `https://st-xxxxxx.arabatapp.com` (null without sub-domains). */
  permanentUrl: z.string().nullable(),
  /** The store's own sub-domain or `/store/{slug}` address. */
  storeUrl: z.string(),
  /** Where shoppers are sent: the primary domain when there is one, else `storeUrl`. */
  primaryUrl: z.string(),
  /** The CNAME target, e.g. `stores.arabatapp.com`. */
  target: z.string().nullable(),
  domain: storeDomainSchema.nullable(),
});
export type StoreAddresses = z.infer<typeof storeAddressesSchema>;

/**
 * `GET /manager/online/domain/search?q=` (ADR-0145): a word (`my shop`) or a name (`myshop.com`).
 * Letters, digits, spaces, dots and dashes only; search only, Arabat never buys the domain.
 */
export const domainSearchQuerySchema = z.object({
  q: z
    .string()
    .max(100)
    .transform(normalizeDomain)
    .pipe(
      z
        .string()
        .min(2, { error: 'Type at least two letters.' })
        .max(63)
        .regex(/^[a-z0-9][a-z0-9 .-]*$/, { error: 'Use English letters, digits and dashes.' }),
    ),
});
/** One name and what the registry says about it (prices: decimal strings, display only). */
const domainOfferSchema = z.object({
  domain: z.string(),
  registrable: z.boolean(),
  reason: z.string().nullable(),
  tier: z.string().nullable(),
  currency: z.string().nullable(),
  registrationCost: z.string().nullable(),
  renewalCost: z.string().nullable(),
});
export type DomainOffer = z.infer<typeof domainOfferSchema>;
export const domainSearchResultSchema = z.object({
  query: z.string(),
  /** The exact name typed (when it was a full name), checked at the registry; else null. */
  exact: domainOfferSchema.nullable(),
  /** Other names, each checked at the registry. */
  suggestions: z.array(domainOfferSchema),
});
export type DomainSearchResult = z.infer<typeof domainSearchResultSchema>;
/** `GET /manager/online/domain/search/status`: whether the search works on this server. */
export const domainSearchStatusSchema = z.object({ available: z.boolean() });

/** `GET /manager/online/slug-availability?slug=`: can this merchant take the address? */
export const slugAvailabilityQuerySchema = z.object({
  slug: z.string().trim().toLowerCase().max(80),
});
export const slugAvailabilitySchema = z.object({
  slug: z.string(),
  available: z.boolean(),
  /** Why not: invalid format, reserved word, taken by another store or link page, or renamed too recently. */
  reason: z.enum(['invalid', 'reserved', 'taken', 'rename_limit']).nullable(),
  /** Free alternatives when the address is not available. */
  suggestions: z.array(z.string()),
});
export type SlugAvailability = z.infer<typeof slugAvailabilitySchema>;

/** Tenders staff may record for a pay-on-receipt order (active POS methods without a gateway). */
export const onlineTenderListSchema = z.object({
  tenders: z.array(
    z.object({
      id: z.string(),
      ...named,
      kind: z.string(),
      /** Non-cash tenders need a reference when the company requires one (BR-181). */
      requiresReference: z.boolean(),
    }),
  ),
});

/** Store image limits (logo, banner, category, collection): same as link-page pictures. */
export const MAX_STORE_IMAGE_BYTES = 2 * 1024 * 1024;

/**
 * The shared catalog category as the store shows it (ADR-0130): one category tree for the point
 * of sale and the store; the store keeps whether it is shown, its order, an optional store name
 * and a picture.
 */
export const storeCategorySchema = z.object({
  id: z.string(),
  /** The shared name (point of sale and store). */
  ...named,
  parentId: z.string().nullable(),
  /** The store's own name; null = the shared name. */
  onlineNameEn: z.string().nullable(),
  onlineNameAr: z.string().nullable(),
  /** Shown in the store's navigation. */
  visible: z.boolean(),
  position: z.number().int(),
  imageUrl: z.string().nullable(),
  /** Active products in this category. */
  productCount: z.number().int(),
  /** Google's title and description of the category's page; null = automatic. */
  ...seoFields,
  /** The page's address in the store, `/categories/{handle}` (migration 0107). */
  handle: z.string(),
  version: z.number().int(),
});
export type StoreCategory = z.infer<typeof storeCategorySchema>;
export const storeCategoryListSchema = z.object({
  categories: z.array(storeCategorySchema),
  /** Active products without a category: shoppers find them only in "All products". */
  uncategorized: z.number().int(),
});
export type StoreCategoryList = z.infer<typeof storeCategoryListSchema>;
/**
 * `POST|PUT /manager/online/categories`: the shared name needs `catalog.manage` (it changes the
 * point of sale too); the store settings need `online.manage`.
 */
export const saveStoreCategorySchema = z
  .object({
    nameEn: text(80),
    nameAr: text(80),
    onlineNameEn: text(80),
    onlineNameAr: text(80),
    visible: z.boolean().default(true),
    /** Google's title and description (absent = unchanged, '' = automatic). */
    ...saveSeoFields,
    /** The page's address (absent = unchanged; a new category is named from its name). */
    handle: storeHandleSchema.optional(),
    version: z.number().int().positive().optional(),
  })
  .refine((c) => Boolean(c.nameAr || c.nameEn), {
    error: 'A category name is required.',
    path: ['nameAr'],
  });
export type SaveStoreCategory = z.input<typeof saveStoreCategorySchema>;
export const storeCategoryOrderSchema = z.object({ ids: z.array(z.uuid()).max(1000) });

/** Marketing collections of the store (ADR-0130): "Offers", "New in"…; a product may be in several. */
const ONLINE_COLLECTION_STATUSES = ['active', 'hidden'] as const;
export const onlineCollectionSchema = z.object({
  id: z.string(),
  ...named,
  position: z.number().int(),
  status: z.enum(ONLINE_COLLECTION_STATUSES),
  imageUrl: z.string().nullable(),
  /** Products in this collection, in the collection's order. */
  productIds: z.array(z.string()),
  /** Google's title and description of the collection's page; null = automatic. */
  ...seoFields,
  /** The page's address in the store, `/collections/{handle}` (migration 0107). */
  handle: z.string(),
  version: z.number().int(),
});
export type OnlineCollection = z.infer<typeof onlineCollectionSchema>;
export const onlineCollectionListSchema = z.object({
  collections: z.array(onlineCollectionSchema),
});
export const saveOnlineCollectionSchema = z
  .object({
    nameEn: text(80),
    nameAr: text(80),
    status: z.enum(ONLINE_COLLECTION_STATUSES).default('active'),
    /** When given, the collection's products in this order (replaces the previous list). */
    productIds: z.array(z.uuid()).max(5000).optional(),
    /** Google's title and description (absent = unchanged, '' = automatic). */
    ...saveSeoFields,
    /** The page's address (absent = unchanged; a new collection is named from its name). */
    handle: storeHandleSchema.optional(),
    version: z.number().int().positive().optional(),
  })
  .refine((c) => Boolean(c.nameAr || c.nameEn), {
    error: 'A collection name is required.',
    path: ['nameAr'],
  });
export type SaveOnlineCollection = z.input<typeof saveOnlineCollectionSchema>;
export const onlineCollectionOrderSchema = z.object({ ids: z.array(z.uuid()).max(500) });

/** Brands of the store (ADR-0146): who makes or roasts a product; a product has at most one. */
export const onlineBrandSchema = z.object({
  id: z.string(),
  ...named,
  position: z.number().int(),
  status: z.enum(ONLINE_COLLECTION_STATUSES),
  imageUrl: z.string().nullable(),
  /** Products of this brand. */
  productIds: z.array(z.string()),
  /** The page's address in the store, `/brands/{handle}` (migration 0107). */
  handle: z.string(),
  version: z.number().int(),
});
export type OnlineBrand = z.infer<typeof onlineBrandSchema>;
export const onlineBrandListSchema = z.object({ brands: z.array(onlineBrandSchema) });
export const saveOnlineBrandSchema = z
  .object({
    nameEn: text(80),
    nameAr: text(80),
    status: z.enum(ONLINE_COLLECTION_STATUSES).default('active'),
    /** When given, exactly these products carry the brand (others lose it). */
    productIds: z.array(z.uuid()).max(5000).optional(),
    /** The page's address (absent = unchanged; a new brand is named from its name). */
    handle: storeHandleSchema.optional(),
    version: z.number().int().positive().optional(),
  })
  .refine((c) => Boolean(c.nameAr || c.nameEn), {
    error: 'A brand name is required.',
    path: ['nameAr'],
  });
export type SaveOnlineBrand = z.input<typeof saveOnlineBrandSchema>;
export const onlineBrandOrderSchema = z.object({ ids: z.array(z.uuid()).max(500) });

const listingSchema = z.object({
  productId: z.string(),
  ...named,
  /**
   * The product page's address in the store, `/products/{handle}` (migration 0107): named from
   * the product when it is made; a rename keeps it.
   */
  handle: z.string(),
  /** Shown to shoppers on the website (the store console's switch, ADR-0130). */
  visible: z.boolean(),
  /** Offered at the point of sale (changed in the point-of-sale management only). */
  posVisible: z.boolean(),
  variants: z.number().int(),
  price: moneySchema.nullable(),
  trackInventory: z.boolean(),
  /** Display order in the store. */
  position: z.number().int(),
  /** What the store says on top of the catalog (ADR-0121); null = the catalog's text. */
  titleEn: z.string().nullable(),
  titleAr: z.string().nullable(),
  descriptionEn: z.string().nullable(),
  descriptionAr: z.string().nullable(),
  featured: z.boolean(),
  /** Units of one variant per order; null = the store default (20). */
  maxPerOrder: z.number().int().nullable(),
  /** The shared catalog category (ADR-0130). */
  category: z.object({ id: z.string(), ...named }).nullable(),
  collectionIds: z.array(z.string()),
  /** The product's brand on the website (ADR-0146). */
  brandId: z.string().nullable().default(null),
  /** Roast / production date shown on the website, `YYYY-MM-DD` (ADR-0147). */
  roastDate: z.string().nullable().default(null),
  /** Additional information rows per language (ADR-0149). */
  detailsAr: z.array(z.object({ name: z.string(), value: z.string() })).default([]),
  detailsEn: z.array(z.object({ name: z.string(), value: z.string() })).default([]),
  /** Option groups the shopper chooses from (ADR-0160), in display order. */
  optionGroupIds: z.array(z.string()).default([]),
  /** What Google shows: title, description and the pictures' text; null = automatic. */
  ...seoFields,
  ...imageAltFields,
  /** Catalog images (managed in the catalog), served by the API. */
  images: z.array(z.object({ id: z.string(), url: z.string() })),
});
export type Listing = z.infer<typeof listingSchema>;
export const listingListSchema = z.object({ products: z.array(listingSchema) });
/** `PUT /manager/online/listings`: bulk actions on selected products. */
export const saveListingsSchema = z
  .object({
    productIds: z.array(z.uuid()).max(5000),
    /** Show on or hide from the website. */
    visible: z.boolean().optional(),
    /** Put the products in this shared category (null = none); needs `catalog.manage`. */
    categoryId: z.uuid().nullable().optional(),
    /** Add the products to this collection. */
    addToCollectionId: z.uuid().optional(),
  })
  .refine(
    (b) =>
      b.visible !== undefined || b.categoryId !== undefined || b.addToCollectionId !== undefined,
    { error: 'Choose an action.', path: ['productIds'] },
  );
export type SaveListings = z.input<typeof saveListingsSchema>;
/** A roast / production date (ADR-0147): a real calendar day, `YYYY-MM-DD`, or null. */
export const roastDateSchema = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, { error: 'A date like 2026-10-27.' })
  .refine((v) => {
    const d = new Date(`${v}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
  }, 'A real date.')
  .nullable();
/** Most additional-information rows of one product in one language (ADR-0149). */
export const DETAILS_MAX = 30;
/**
 * Additional information of a product (ADR-0149): name / value rows such as "Origin: Brazil",
 * shown in the product page's "Additional information" tab. Plain text, never markup.
 */
export const productDetailsSchema = z
  .array(
    z.object({
      // No colon in a name: the Excel cell splits "name: value" at the first one.
      name: z
        .string()
        .trim()
        .min(1)
        .max(60)
        .regex(/^[^:：]+$/, { error: 'A name without a colon.' }),
      value: z.string().trim().min(1).max(300),
    }),
  )
  .max(DETAILS_MAX);
export type ProductDetail = z.infer<typeof productDetailsSchema>[number];
/** `PUT /manager/online/listings/:productId`: the store's own text and options for one product. */
export const saveListingSchema = z.object({
  titleEn: text(120),
  titleAr: text(120),
  descriptionEn: text(2000),
  descriptionAr: text(2000),
  featured: z.boolean(),
  maxPerOrder: z.number().int().min(1).max(20).nullable(),
  visible: z.boolean().optional(),
  /** The shared category; a change needs `catalog.manage`. Omitted = unchanged. */
  categoryId: z.uuid().nullable().optional(),
  collectionIds: z.array(z.uuid()).max(100).optional(),
  /** The product's brand (ADR-0146); null = none. Omitted = unchanged. */
  brandId: z.uuid().nullable().optional(),
  /** Roast / production date, `YYYY-MM-DD` (ADR-0147); null = none. Omitted = unchanged. */
  roastDate: roastDateSchema.optional(),
  /** Additional information rows (ADR-0149). Omitted = unchanged. */
  detailsAr: productDetailsSchema.optional(),
  detailsEn: productDetailsSchema.optional(),
  /** Option groups of the product, in display order (ADR-0160). Omitted = unchanged. */
  optionGroupIds: z.array(z.uuid()).max(20).optional(),
  /** What Google shows (owner decision 2026-11-03). Omitted = unchanged, '' = automatic. */
  ...saveSeoFields,
  /** The product page's address (migration 0107). Omitted = unchanged; taken → 409 DUPLICATE_HANDLE. */
  handle: storeHandleSchema.optional(),
  imageAltAr: keptText(IMAGE_ALT_MAX),
  imageAltEn: keptText(IMAGE_ALT_MAX),
});
export type SaveListing = z.input<typeof saveListingSchema>;
/** `PUT /manager/online/listings/order`: every active product exactly once, in display order. */
export const listingOrderSchema = z.object({ productIds: z.array(z.uuid()).max(5000) });

// ---------------------------------------------------------------- orders (shared shapes)
/** A choice the shopper made on an order line (ADR-0160), as it was at checkout. */
export const orderLineOptionSchema = z.object({
  optionId: z.string(),
  groupNameEn: z.string().nullable(),
  groupNameAr: z.string().nullable(),
  ...named,
  /** Added to the line's unit price (already included in `unitPrice`). */
  price: moneySchema,
});
export type OrderLineOption = z.infer<typeof orderLineOptionSchema>;
const onlineOrderLineSchema = z.object({
  position: z.number().int(),
  variantId: z.string(),
  productId: z.string(),
  ...itemNames,
  sku: z.string().nullable(),
  quantity: z.string(),
  /** Includes the prices of the chosen options. */
  unitPrice: moneySchema,
  lineTotal: moneySchema,
  options: z.array(orderLineOptionSchema).default([]),
  /** The shopper's note for this item (ADR-0160). */
  note: z.string().nullable().default(null),
});

/**
 * The public invoice of an online order (ADR-0138 amendment 7): exactly what the A4 invoice
 * shows (amounts in minor units as decimal strings), opened with the store's permanent code and
 * the order's tracking token, without a session.
 */
export const publicInvoiceSchema = z.object({
  storeName: z.string(),
  logoUrl: z.string().nullable(),
  accent: z.string().nullable(),
  storePhone: z.string().nullable(),
  storeEmail: z.string().nullable(),
  branchName: z.string().nullable(),
  branchAddress: z.string().nullable(),
  branchPhone: z.string().nullable(),
  number: z.string(),
  receiptNumber: z.string().nullable(),
  issuedAt: z.string(),
  state: z.enum(['paid', 'unpaid', 'cancelled']),
  paidAt: z.string().nullable(),
  paymentMethod: z.string().nullable(),
  paymentReference: z.string().nullable(),
  fulfillment: z.enum(FULFILLMENTS),
  wanted: z.string().nullable(),
  customerName: z.string(),
  customerPhone: z.string().nullable(),
  customerEmail: z.string().nullable(),
  deliveryArea: z.string().nullable(),
  deliveryAddress: z.string().nullable(),
  note: z.string().nullable(),
  lines: z.array(
    z.object({
      name: z.string(),
      detail: z.string().nullable(),
      /** Chosen options as "Group: choice" (ADR-0160). */
      options: z.array(z.string()).default([]),
      note: z.string().nullable().default(null),
      quantity: z.string(),
      unitPriceMinor: z.string(),
      totalMinor: z.string(),
    }),
  ),
  subtotalMinor: z.string(),
  discountMinor: z.string(),
  promotion: z.string().nullable(),
  deliveryFeeMinor: z.string(),
  totalMinor: z.string(),
});
export type PublicInvoice = z.infer<typeof publicInvoiceSchema>;

export const publicOrderSchema = z.object({
  number: z.string(),
  status: z.enum(ONLINE_ORDER_STATUSES),
  fulfillment: z.enum(FULFILLMENTS),
  customerName: z.string(),
  lines: z.array(onlineOrderLineSchema),
  subtotal: moneySchema,
  /** Order discount of the applied promotion (ADR-0125); zero without one. */
  discount: moneySchema,
  promotion: z.object({ code: z.string().nullable(), name: z.string() }).nullable(),
  deliveryFee: moneySchema,
  total: moneySchema,
  createdAt: z.string(),
  paymentMode: z.enum(PAYMENT_MODES),
  paidAt: z.string().nullable(),
  /** Gateway orders only: when the stock reservation ends. */
  reservedUntil: z.string().nullable(),
  trackingToken: z.string(),
  /** Where to send the customer to pay (while pending payment). */
  paymentUrl: z.string().nullable(),
  /** Requested slot start (ADR-0123); null = as soon as possible. */
  scheduledFor: z.string().nullable(),
  /** The area's name as it was at checkout (kept if the area is deleted). */
  deliveryAreaName: z.string().nullable(),
  /** The area's current names while it exists (for the visitor's language). */
  deliveryArea: z.object(named).nullable(),
  /** The governorate area the shopper picked (ADR-0140); null for zones without places. */
  deliveryPlace: z
    .object({ ...named, region: z.object(named) })
    .nullable()
    .default(null),
  /** The branch that prepares the order (pickup address for the shopper). */
  branch: z.object({ ...named, address: z.string().nullable(), phone: z.string().nullable() }),
  timeline: z.array(z.object({ status: z.enum(ONLINE_ORDER_STATUSES), at: z.string() })),
});
export type PublicOrder = z.infer<typeof publicOrderSchema>;

export const onlineOrderSchema = publicOrderSchema.extend({
  id: z.string(),
  /** The invoice page at the store's own address, e.g. `https://shop.example/orders/<token>/invoice`. */
  invoiceUrl: z.string(),
  branchId: z.string(),
  customerPhone: z.string(),
  customerEmail: z.string().nullable(),
  deliveryAddress: z.string().nullable(),
  deliveryAreaId: z.string().nullable(),
  note: z.string().nullable(),
  customerId: z.string().nullable(),
  saleId: z.string().nullable(),
  receiptNumber: z.string().nullable(),
  confirmedAt: z.string().nullable(),
  /** Null for a pay-on-receipt order whose payment is not recorded yet. */
  paymentMethod: z.object(named).extend({ provider: z.string() }).nullable(),
  /** Reference of the tender recorded by staff (pay on receipt). */
  paymentReference: z.string().nullable(),
  payments: z.array(
    z.object({
      id: z.string(),
      provider: z.string(),
      providerReference: z.string().nullable(),
      status: z.enum(GATEWAY_STATUSES),
      amount: moneySchema,
      failureReason: z.string().nullable(),
      createdAt: z.string(),
      completedAt: z.string().nullable(),
    }),
  ),
  statusNote: z.string().nullable(),
  version: z.number().int(),
});
export type OnlineOrder = z.infer<typeof onlineOrderSchema>;
export const onlineOrderListSchema = z.object({
  orders: z.array(
    onlineOrderSchema.omit({ lines: true, payments: true, timeline: true, invoiceUrl: true }),
  ),
  ...pageFields,
  counts: z.record(z.string(), z.number().int()),
});
export const onlineOrderListQuerySchema = z.object({
  status: z.enum(ONLINE_ORDER_STATUSES).optional(),
  ...pageQuery(25),
});
export type OnlineOrderListQuery = z.input<typeof onlineOrderListQuerySchema>;
const ONLINE_ORDER_ACTIONS = [
  'confirm',
  'mark_paid',
  'preparing',
  'ready',
  'out_for_delivery',
  'completed',
  'cancel',
] as const;
export type OnlineOrderAction = (typeof ONLINE_ORDER_ACTIONS)[number];
export const onlineOrderActionSchema = z
  .object({
    action: z.enum(ONLINE_ORDER_ACTIONS),
    note: text(300),
    /** `mark_paid`: the tender the money was received with. */
    paymentMethodId: z.uuid().nullish(),
    /** `mark_paid`: reference of a non-cash tender. */
    reference: text(80),
    version: z.number().int().positive(),
  })
  .refine((a) => a.action !== 'mark_paid' || Boolean(a.paymentMethodId), {
    error: 'Choose the payment method.',
    path: ['paymentMethodId'],
  });
export type OnlineOrderActionInput = z.input<typeof onlineOrderActionSchema>;

// ---------------------------------------------------------------- delivery areas and branches (ADR-0123)
export const onlineDeliveryAreaSchema = z.object({
  id: z.string(),
  ...named,
  fee: moneySchema,
  minimumOrder: moneySchema,
  /** Orders at or above this amount are delivered free; null = never. */
  freeDeliveryFrom: moneySchema.nullable(),
  estimatedMinutes: z.number().int().nullable(),
  /** Serving branch; null = the store's fulfilment branch. */
  branchId: z.string().nullable(),
  /** Reference places this zone covers (ADR-0140); empty = a named area without places. */
  placeIds: z.array(z.string()).default([]),
  position: z.number().int(),
  active: z.boolean(),
  version: z.number().int(),
});
export type OnlineDeliveryArea = z.infer<typeof onlineDeliveryAreaSchema>;
export const onlineDeliveryAreaListSchema = z.object({
  areas: z.array(onlineDeliveryAreaSchema),
});
export const saveOnlineDeliveryAreaSchema = z
  .object({
    nameEn: text(80),
    nameAr: text(80),
    fee: moneyAmountSchema,
    minimumOrder: moneyAmountSchema.default('0.000'),
    freeDeliveryFrom: moneyAmountSchema.nullish().transform((v) => v ?? null),
    estimatedMinutes: z
      .number()
      .int()
      .min(1)
      .max(1440)
      .nullish()
      .transform((v) => v ?? null),
    branchId: optionalId(),
    /** Reference places the zone covers (ADR-0140); each place in one zone of the store. */
    placeIds: z
      .array(z.uuid())
      .max(500)
      .default([])
      .transform((ids) => [...new Set(ids)]),
    active: z.boolean().default(true),
    version: z.number().int().positive().optional(),
  })
  .refine((a) => Boolean(a.nameAr || a.nameEn), {
    error: 'An area name is required.',
    path: ['nameAr'],
  });
export type SaveOnlineDeliveryArea = z.input<typeof saveOnlineDeliveryAreaSchema>;
export const onlineDeliveryAreaOrderSchema = z.object({ ids: z.array(z.uuid()).max(500) });

/** The reference places a zone can cover (ADR-0140): governorates and their areas. */
export const geoPlaceListSchema = z.object({
  regions: z.array(
    z.object({
      id: z.string(),
      ...named,
      places: z.array(z.object({ id: z.string(), ...named })),
    }),
  ),
});
export type GeoPlaceList = z.infer<typeof geoPlaceListSchema>;

export const onlineStoreBranchSchema = z.object({
  branchId: z.string(),
  ...named,
  address: z.string().nullable(),
  phone: z.string().nullable(),
  pickupEnabled: z.boolean(),
  deliveryEnabled: z.boolean(),
  preparationMinutes: z.number().int(),
  /** Pickup (opening) hours; null = always open (ADR-0141). */
  hours: hoursSchema,
  /** Delivery hours; null = the same as `hours`. */
  deliveryHours: hoursSchema.default(null),
  /** Booking ahead at this branch (ADR-0141). */
  schedulingEnabled: z.boolean().default(false),
  /** Minutes between two slots. */
  slotMinutes: z.number().int().default(30),
  scheduleDays: z.number().int().default(7),
  slotCapacity: z.number().int().nullable().default(null),
  position: z.number().int(),
  active: z.boolean(),
  version: z.number().int(),
});
export type OnlineStoreBranch = z.infer<typeof onlineStoreBranchSchema>;
export const onlineStoreBranchListSchema = z.object({
  branches: z.array(onlineStoreBranchSchema),
});
export const saveOnlineStoreBranchSchema = z
  .object({
    pickupEnabled: z.boolean().default(true),
    deliveryEnabled: z.boolean().default(false),
    preparationMinutes: z.number().int().min(0).max(1440).default(30),
    hours: hoursSchema.default(null),
    deliveryHours: hoursSchema.default(null),
    schedulingEnabled: z.boolean().default(false),
    slotMinutes: z.number().int().min(15).max(240).default(30),
    scheduleDays: z.number().int().min(1).max(14).default(7),
    slotCapacity: z
      .number()
      .int()
      .min(1)
      .max(1000)
      .nullish()
      .transform((v) => v ?? null),
    active: z.boolean().default(true),
    version: z.number().int().positive().optional(),
  })
  .refine((b) => b.pickupEnabled || b.deliveryEnabled, {
    error: 'Enable pickup or delivery.',
    path: ['pickupEnabled'],
  });
export type SaveOnlineStoreBranch = z.input<typeof saveOnlineStoreBranchSchema>;
export const onlineStoreBranchOrderSchema = z.object({ branchIds: z.array(z.uuid()).max(200) });

// ---------------------------------------------------------------- POS hand-over (ADR-0123)
export const posOnlineOrderSchema = z.object({
  id: z.string(),
  number: z.string(),
  status: z.enum(ONLINE_ORDER_STATUSES),
  fulfillment: z.enum(FULFILLMENTS),
  paymentMode: z.enum(PAYMENT_MODES),
  customerName: z.string(),
  customerPhone: z.string(),
  total: moneySchema,
  /** Set once the money was received (gateway, console or till). */
  paid: z.boolean(),
  scheduledFor: z.string().nullable(),
  createdAt: z.string(),
  lines: z.array(onlineOrderLineSchema),
  note: z.string().nullable(),
  version: z.number().int(),
});
export type PosOnlineOrder = z.infer<typeof posOnlineOrderSchema>;
export const posOnlineOrderListSchema = z.object({ orders: z.array(posOnlineOrderSchema) });
export const posOnlineOrderPaySchema = z.object({
  idempotencyKey: z.uuid(),
  payments: z.array(paymentInputSchema).min(1).max(10),
  /** The total shown to the cashier; refused if the order changed meanwhile. */
  expectedTotal: moneyAmountSchema,
  version: z.number().int().positive(),
});
export type PosOnlineOrderPay = z.input<typeof posOnlineOrderPaySchema>;
export const posOnlineOrderCompleteSchema = z.object({ version: z.number().int().positive() });

// ---------------------------------------------------------------- storefront (public)
/** A page's handle in the storefront payload (migration 0107); null = linked by its id. */
const storeGroupHandle = z.string().nullable().default(null);
export const storefrontSchema = z.object({
  slug: z.string(),
  ...named,
  taglineEn: z.string().nullable(),
  taglineAr: z.string().nullable(),
  announcementEn: z.string().nullable(),
  announcementAr: z.string().nullable(),
  primaryColor: z.string(),
  contactPhone: z.string().nullable(),
  contactEmail: z.string().nullable(),
  /** paused: browsing only, no new orders. */
  acceptingOrders: z.boolean(),
  pickupEnabled: z.boolean(),
  deliveryEnabled: z.boolean(),
  deliveryFee: moneySchema,
  minimumOrder: moneySchema,
  paymentMethods: z.array(z.object({ id: z.string(), ...named, provider: z.string() })),
  /** Orders may be placed without paying online (collected on pickup / delivery). */
  payOnReceipt: z.boolean(),
  logoUrl: z.string().nullable(),
  bannerUrl: z.string().nullable(),
  /** The shared catalog categories shown in the store, in the store's order (ADR-0130). */
  categories: z.array(
    z.object({
      id: z.string(),
      ...named,
      parentId: z.string().nullable(),
      imageUrl: z.string().nullable(),
      handle: storeGroupHandle,
    }),
  ),
  /** Active collections that hold products shown on the website. */
  collections: z.array(
    z.object({
      id: z.string(),
      ...named,
      imageUrl: z.string().nullable(),
      handle: storeGroupHandle,
    }),
  ),
  /** Active brands that have products shown on the website (ADR-0146). */
  brands: z
    .array(
      z.object({
        id: z.string(),
        ...named,
        imageUrl: z.string().nullable(),
        handle: storeGroupHandle,
      }),
    )
    .default([]),
  /** Branches serving the store (ADR-0123); one entry = the single fulfilment branch. */
  branches: z.array(
    z.object({
      id: z.string(),
      ...named,
      address: z.string().nullable(),
      phone: z.string().nullable(),
      pickupEnabled: z.boolean(),
      deliveryEnabled: z.boolean(),
      preparationMinutes: z.number().int(),
      hours: hoursSchema,
      /** Open right now by its hours (always true without hours). */
      openNow: z.boolean(),
      /** Delivering right now by its delivery hours (ADR-0141). */
      deliveryOpenNow: z.boolean().default(true),
      /** Booking ahead at this branch (ADR-0141). */
      scheduling: z
        .object({ enabled: z.boolean(), slotMinutes: z.number().int(), days: z.number().int() })
        .default({ enabled: false, slotMinutes: 30, days: 7 }),
    }),
  ),
  /** Products, stock and prices come from this branch unless the shopper picks another. */
  defaultBranchId: z.string(),
  /** Delivery areas to pick from (ADR-0123); empty = the flat `deliveryFee` applies. */
  deliveryAreas: z.array(
    z.object({
      id: z.string(),
      ...named,
      fee: moneySchema,
      minimumOrder: moneySchema,
      freeDeliveryFrom: moneySchema.nullable(),
      estimatedMinutes: z.number().int().nullable(),
      branchId: z.string(),
      /** The governorate areas this zone covers (ADR-0140); empty = pick the zone itself. */
      places: z.array(z.object({ id: z.string(), ...named, regionId: z.string() })).default([]),
    }),
  ),
  /** Governorates of the places above, in their usual order (ADR-0140). */
  deliveryRegions: z.array(z.object({ id: z.string(), ...named })).default([]),
  scheduling: z.object({
    enabled: z.boolean(),
    slotMinutes: z.number().int(),
    /** Days that can be chosen, today included (ADR-0123 amendment). */
    days: z.number().int().default(7),
  }),
  timezone: z.string(),
  /** Shoppers may sign in (ADR-0124). */
  accountsEnabled: z.boolean(),
  /** When the shopper picks the delivery area (ADR-0141). */
  areaPrompt: z.enum(AREA_PROMPTS).default('checkout'),
  /** Google Search Console verification token for the page's meta tag; null = none. */
  googleSiteVerification: z.string().nullable().default(null),
});
export type Storefront = z.infer<typeof storefrontSchema>;
export const storeSlotListSchema = z.object({
  slots: z.array(z.object({ start: z.string(), end: z.string(), available: z.boolean() })),
});
export type StoreSlotList = z.infer<typeof storeSlotListSchema>;

export const storeProductSchema = z.object({
  productId: z.string(),
  /** The page's address, `/products/{handle}` (migration 0107); null = linked by its id. */
  handle: z.string().nullable().default(null),
  /** The store's title when set, else the catalog name (same for the description). */
  ...named,
  descriptionEn: z.string().nullable(),
  descriptionAr: z.string().nullable(),
  /** Id in the storefront's `categories`; null = none shown. */
  categoryId: z.string().nullable(),
  /** The category and every shown parent above it (ADR-0146). */
  categoryIds: z.array(z.string()).default([]),
  /** Ids in the storefront's `collections`. */
  collectionIds: z.array(z.string()),
  /** Id in the storefront's `brands` (ADR-0146); null = none. */
  brandId: z.string().nullable().default(null),
  /** Roast / production date the merchant shows, `YYYY-MM-DD` (ADR-0147); null = none. */
  roastDate: z.string().nullable().default(null),
  /** Additional information rows per language (ADR-0149); the store shows the shopper's language. */
  detailsAr: z.array(z.object({ name: z.string(), value: z.string() })).default([]),
  detailsEn: z.array(z.object({ name: z.string(), value: z.string() })).default([]),
  /**
   * The merchant's title and description for Google (null = automatic), kept in step by the
   * page's own title, and the pictures' alternative text (null = the product's name).
   */
  ...seoFields,
  ...imageAltFields,
  /**
   * Choices the shopper makes for this product (ADR-0160): roast, grind, size of a topping…
   * `minSelect` 0 = optional; each option's price is added to the unit price.
   */
  optionGroups: z
    .array(
      z.object({
        id: z.string(),
        ...named,
        minSelect: z.number().int(),
        maxSelect: z.number().int(),
        /** How the product page shows a choice's price (ADR-0160 amendment 1). */
        priceDisplay: z.enum(['difference', 'full', 'hidden']).default('difference'),
        options: z.array(z.object({ id: z.string(), ...named, price: moneySchema })),
      }),
    )
    .default([]),
  /** When the product was added to the catalog (the "newest" order and the "new" badge). */
  createdAt: z.string().nullable().default(null),
  imageUrl: z.string().nullable(),
  /** Every catalog image, first one = `imageUrl`. */
  images: z.array(z.string()),
  featured: z.boolean(),
  position: z.number().int(),
  /**
   * Place among the best sellers (1 = most sold): units sold less units refunded over the last
   * 30 days, every branch and channel; only the first 24, null otherwise. The counts stay private.
   */
  soldRank: z.number().int().nullable().default(null),
  variants: z.array(
    z.object({
      variantId: z.string(),
      ...named,
      sku: z.string().nullable(),
      /** The price now: the discounted one while a product discount is in force (ADR-0133). */
      price: moneySchema,
      /** The price before the discount, only while one is in force (shown struck through). */
      regularPrice: moneySchema.nullable().default(null),
      /** When the discount in force ends, if it has an end. */
      discountEndsAt: z.string().nullable().default(null),
      /** Whether it can be ordered now (stock minus reservations, or untracked). */
      available: z.boolean(),
      /** Upper bound offered in the cart (never the exact stock). */
      maxQuantity: z.number().int(),
      /**
       * Units left, only when at or under the store's "only N left" setting (ADR-0128
       * amendment 4); null otherwise, so the stock stays private unless the merchant shows it.
       */
      fewLeft: z.number().int().nullable().default(null),
    }),
  ),
});
export type StoreProduct = z.infer<typeof storeProductSchema>;
export const storeProductListSchema = z.object({ products: z.array(storeProductSchema) });

/**
 * One cart line (ADR-0160): the variant, its quantity, the options chosen for it and the
 * shopper's note. The same variant may appear on several lines with different choices.
 */
export const storeCartLineSchema = z.object({
  variantId: z.uuid(),
  quantity: quantitySchema,
  optionIds: z.array(z.uuid()).max(50).default([]),
  note: text(200),
});
export const storeCheckoutSchema = z
  .object({
    idempotencyKey: z.uuid(),
    lines: z.array(storeCartLineSchema).min(1).max(50),
    fulfillment: z.enum(FULFILLMENTS),
    customer: z.object({
      name: z.string().trim().min(1).max(120),
      phone: z.string().trim().min(6).max(30),
      email: z
        .email()
        .nullish()
        .or(z.literal(''))
        .transform((v) => v || null),
    }),
    deliveryAddress: text(300),
    /** The branch that prepares the order (ADR-0123); null = the store's default branch. */
    branchId: optionalId(),
    /** Required when the store defines delivery areas and the order is delivered. */
    deliveryAreaId: optionalId(),
    /** The governorate area within that zone; required when the zone covers places (ADR-0140). */
    deliveryPlaceId: optionalId(),
    /** Requested slot start (ISO 8601); null = as soon as possible. */
    scheduledFor: z.iso
      .datetime({ offset: true })
      .nullish()
      .transform((v) => v ?? null),
    note: text(300),
    /** A promotion code entered by the shopper (ADR-0125); automatic offers need none. */
    promotionCode: z
      .string()
      .trim()
      .toUpperCase()
      .max(20)
      .nullish()
      .transform((v) => v || null),
    /** Signed-in shoppers: keep the delivery address on the account (ADR-0124). */
    saveAddress: z.boolean().default(false),
    /** An online method, or null to pay on receipt (when the store allows it). */
    paymentMethodId: z
      .uuid()
      .nullish()
      .transform((v) => v ?? null),
    /** The total the customer saw; refused if prices changed. */
    expectedTotal: moneyAmountSchema,
  })
  .refine((c) => c.fulfillment !== 'delivery' || c.deliveryAddress !== null, {
    error: 'Enter the delivery address.',
    path: ['deliveryAddress'],
  });
export type StoreCheckout = z.input<typeof storeCheckoutSchema>;
export const storeCheckoutResultSchema = z.object({ order: publicOrderSchema });

// ---------------------------------------------------------------- quote (ADR-0125)
export const storeQuoteSchema = z.object({
  lines: z.array(storeCartLineSchema).min(1).max(50),
  fulfillment: z.enum(FULFILLMENTS),
  deliveryAreaId: optionalId(),
  deliveryPlaceId: optionalId(),
  branchId: optionalId(),
  promotionCode: z
    .string()
    .trim()
    .toUpperCase()
    .max(20)
    .nullish()
    .transform((v) => v || null),
});
export type StoreQuote = z.input<typeof storeQuoteSchema>;
export const storeQuoteResultSchema = z.object({
  subtotal: moneySchema,
  discount: moneySchema,
  deliveryFee: moneySchema,
  total: moneySchema,
  /** The promotion applied (a code, or the best automatic offer), or none. */
  promotion: z.object({ code: z.string().nullable(), ...named }).nullable(),
  /** Why the entered code was not applied, if it was not. */
  codeIssue: z
    .enum([
      'unknown',
      'inactive',
      'expired',
      'not_started',
      'minimum',
      'quantity',
      'used_up',
      'customer_limit',
      'no_match',
    ])
    .nullable(),
});
export type StoreQuoteResult = z.infer<typeof storeQuoteResultSchema>;

// ---------------------------------------------------------------- shopper accounts (ADR-0124)
const passwordSchema = z.string().min(8).max(100);
const customerAccountSchema = z.object({
  id: z.string(),
  fullName: z.string(),
  phone: z.string(),
  email: z.string(),
  createdAt: z.string(),
});
export type CustomerAccount = z.infer<typeof customerAccountSchema>;
const customerAddressSchema = z.object({
  id: z.string(),
  label: z.string().nullable(),
  deliveryAreaId: z.string().nullable(),
  deliveryPlaceId: z.string().nullable().default(null),
  address: z.string(),
  isDefault: z.boolean(),
});
export type CustomerAddress = z.infer<typeof customerAddressSchema>;
export const accountViewSchema = z.object({
  account: customerAccountSchema,
  addresses: z.array(customerAddressSchema),
});
export type AccountView = z.infer<typeof accountViewSchema>;
export const accountSessionSchema = z.object({ token: z.string(), account: customerAccountSchema });
export type AccountSession = z.infer<typeof accountSessionSchema>;
export const accountRegisterSchema = z.object({
  fullName: z.string().trim().min(1).max(120),
  phone: z.string().trim().min(6).max(30),
  email: z.email().trim().toLowerCase().max(254),
  password: passwordSchema,
});
export type AccountRegister = z.input<typeof accountRegisterSchema>;
export const accountLoginSchema = z.object({
  email: z.email().trim().toLowerCase().max(254),
  password: z.string().min(1).max(100),
});
export type AccountLogin = z.input<typeof accountLoginSchema>;
export const accountUpdateSchema = z.object({
  fullName: z.string().trim().min(1).max(120),
  phone: z.string().trim().min(6).max(30),
});
export type AccountUpdate = z.input<typeof accountUpdateSchema>;
export const accountPasswordSchema = z.object({
  current: z.string().min(1).max(100),
  next: passwordSchema,
});
export type AccountPassword = z.input<typeof accountPasswordSchema>;
export const accountForgotSchema = z.object({ email: z.email().trim().toLowerCase().max(254) });
export const accountResetSchema = z.object({
  token: z.string().min(10).max(200),
  password: passwordSchema,
});
export type AccountReset = z.input<typeof accountResetSchema>;
export const saveCustomerAddressSchema = z.object({
  label: text(40),
  deliveryAreaId: optionalId(),
  deliveryPlaceId: optionalId(),
  address: z.string().trim().min(1).max(300),
  isDefault: z.boolean().default(false),
});
export type SaveCustomerAddress = z.input<typeof saveCustomerAddressSchema>;
export const accountOrderListSchema = z.object({
  orders: z.array(
    publicOrderSchema.omit({ lines: true, timeline: true }).extend({ lineCount: z.number().int() }),
  ),
});
export type AccountOrderList = z.infer<typeof accountOrderListSchema>;

// ---------------------------------------------------------------- promotions (ADR-0125, manager)
const PROMOTION_KINDS = ['code', 'automatic'] as const;
const PROMOTION_TYPES = ['percentage', 'fixed', 'free_delivery'] as const;
const PROMOTION_TARGETS = ['order', 'category', 'product'] as const;
export const onlinePromotionSchema = z.object({
  id: z.string(),
  ...named,
  kind: z.enum(PROMOTION_KINDS),
  code: z.string().nullable(),
  discountType: z.enum(PROMOTION_TYPES),
  percent: z.string().nullable(),
  amount: moneySchema.nullable(),
  minimumOrder: moneySchema,
  /** Units of the promotion's products the order must hold (ADR-0146); null = none. */
  minimumQuantity: z.number().int().nullable().default(null),
  maxUses: z.number().int().nullable(),
  usesCount: z.number().int(),
  maxUsesPerCustomer: z.number().int().nullable(),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  status: z.enum(['active', 'paused']),
  appliesTo: z.enum(PROMOTION_TARGETS),
  targetIds: z.array(z.string()),
  /** Also discounts items already lowered by a product discount (ADR-0133); off by default. */
  stacksWithDiscounts: z.boolean(),
  position: z.number().int(),
  version: z.number().int(),
});
export type OnlinePromotion = z.infer<typeof onlinePromotionSchema>;
export const onlinePromotionListSchema = z.object({ promotions: z.array(onlinePromotionSchema) });
export const saveOnlinePromotionSchema = z
  .object({
    nameEn: text(80),
    nameAr: text(80),
    kind: z.enum(PROMOTION_KINDS).default('code'),
    code: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z0-9]{3,20}$/, { error: 'Letters and digits, 3–20 characters.' })
      .nullish()
      .transform((v) => v || null),
    discountType: z.enum(PROMOTION_TYPES),
    percent: z
      .string()
      .regex(/^\d{1,3}(\.\d{1,2})?$/)
      .nullish()
      .transform((v) => v || null),
    amount: moneyAmountSchema.nullish().transform((v) => v || null),
    minimumOrder: moneyAmountSchema.default('0.000'),
    /** "Buy 3": units of the promotion's own products (ADR-0146); null = no minimum. */
    minimumQuantity: z
      .number()
      .int()
      .min(1)
      .max(999)
      .nullish()
      .transform((v) => v ?? null),
    maxUses: z
      .number()
      .int()
      .min(1)
      .nullish()
      .transform((v) => v ?? null),
    maxUsesPerCustomer: z
      .number()
      .int()
      .min(1)
      .nullish()
      .transform((v) => v ?? null),
    startsAt: z.iso
      .datetime({ offset: true })
      .nullish()
      .transform((v) => v ?? null),
    endsAt: z.iso
      .datetime({ offset: true })
      .nullish()
      .transform((v) => v ?? null),
    status: z.enum(['active', 'paused']).default('active'),
    appliesTo: z.enum(PROMOTION_TARGETS).default('order'),
    targetIds: z.array(z.uuid()).max(200).default([]),
    stacksWithDiscounts: z.boolean().default(false),
    version: z.number().int().positive().optional(),
  })
  .refine((p) => Boolean(p.nameAr || p.nameEn), { error: 'A name is required.', path: ['nameAr'] })
  .refine((p) => p.kind !== 'code' || p.code !== null, { error: 'Enter the code.', path: ['code'] })
  .refine(
    (p) =>
      p.discountType !== 'percentage' ||
      (p.percent !== null && Number(p.percent) > 0 && Number(p.percent) <= 100),
    {
      error: 'Enter a percentage between 0 and 100.',
      path: ['percent'],
    },
  )
  .refine((p) => p.discountType !== 'fixed' || (p.amount !== null && Number(p.amount) > 0), {
    error: 'Enter the discount amount.',
    path: ['amount'],
  })
  .refine((p) => p.appliesTo === 'order' || p.targetIds.length > 0, {
    error: 'Choose at least one category or product.',
    path: ['targetIds'],
  });
export type SaveOnlinePromotion = z.input<typeof saveOnlinePromotionSchema>;
export const onlinePromotionOrderSchema = z.object({ ids: z.array(z.uuid()).max(500) });

// ---------------------------------------------------------------- mock gateway (test only)
export const mockPaymentSchema = z.object({
  paymentId: z.string(),
  merchantName: z.string(),
  orderNumber: z.string(),
  amount: moneySchema,
  status: z.enum(GATEWAY_STATUSES),
  returnUrl: z.string(),
});
export type MockPayment = z.infer<typeof mockPaymentSchema>;
export const mockPaymentOutcomeSchema = z.object({
  outcome: z.enum(['succeeded', 'failed', 'cancelled']),
});
